import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Loader2, MessageSquarePlus, Send, Users, X } from "lucide-react";
import { toast } from "sonner";
import { addComment, getCowriterName, getRoom, resolveComment, setCowriterName } from "@/lib/writingRoom";
import { safeApiError } from "@/lib/safeErrorText";

const POLL_MS = 6000;

function parse(raw) {
  const out = [];
  let cur = { header: null, lines: [] };
  for (const ln of String(raw || "").split(/\r?\n/)) {
    const m = ln.trim().match(/^\[(.+?)\]\s*$/);
    if (m) { if (cur.header !== null || cur.lines.length) out.push(cur); cur = { header: m[1], lines: [] }; } else cur.lines.push(ln);
  }
  if (cur.header !== null || cur.lines.length) out.push(cur);
  return out;
}

function CommentForm({ lineKey, lineText, onSubmit, onCancel, busy }) {
  const [author, setAuthor] = useState(getCowriterName());
  const [text, setText] = useState("");
  const [suggestion, setSuggestion] = useState("");
  const field = "w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/35 focus:border-violet-400/60 focus:outline-none";
  const submit = (e) => {
    e.preventDefault();
    if (!author.trim() || !text.trim()) { toast.error("Add your name and a comment"); return; }
    setCowriterName(author.trim());
    onSubmit({ line_key: lineKey, line_text: lineText, author: author.trim(), text: text.trim(), suggestion: suggestion.trim() });
  };
  return (
    <form data-testid="room-comment-form" onSubmit={submit} className="mt-2 space-y-2 rounded-xl border border-violet-400/30 bg-violet-500/[0.08] p-3">
      {lineText ? <p className="truncate text-xs italic text-white/60">On: “{lineText}”</p> : <p className="text-xs text-white/60">General comment on the whole song</p>}
      <input data-testid="room-comment-author" value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Your name" maxLength={60} className={field} />
      <textarea data-testid="room-comment-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="What would you change, and why?" rows={2} maxLength={1500} className={field} />
      {lineText ? <input data-testid="room-comment-suggestion" value={suggestion} onChange={(e) => setSuggestion(e.target.value)} placeholder="Suggested replacement line (optional)" maxLength={400} className={field} /> : null}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-lg px-3 py-1.5 text-sm text-white/70 hover:text-white">Cancel</button>
        <button type="submit" data-testid="room-comment-submit" disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg bg-violet-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-violet-400 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Post
        </button>
      </div>
    </form>
  );
}

/** /write/:roomId — a co-writer reads the lyrics and leaves line comments. */
export default function WritingRoom() {
  const { roomId } = useParams();
  const [room, setRoom] = useState(null);
  const [error, setError] = useState(null);
  const [target, setTarget] = useState(null); // {lineKey, lineText}
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try { setRoom(await getRoom(roomId)); setError(null); }
    catch (e) { setError(e?.response?.status === 404 ? "This writing room doesn't exist or has expired." : safeApiError(e, "Could not load the room")); }
  }, [roomId]);

  useEffect(() => { load(); timer.current = setInterval(load, POLL_MS); return () => clearInterval(timer.current); }, [load]);

  const sections = useMemo(() => parse(room?.lyrics), [room?.lyrics]);
  const byLine = useMemo(() => {
    const m = {};
    for (const c of room?.comments || []) (m[c.line_key] = m[c.line_key] || []).push(c);
    return m;
  }, [room?.comments]);

  const submit = async (body) => {
    setBusy(true);
    try { await addComment(roomId, body); setTarget(null); await load(); toast.success("Comment posted"); }
    catch (e) { toast.error("Could not post", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };

  const resolve = async (c) => {
    try { await resolveComment(roomId, c.id); await load(); } catch (e) { toast.error("Could not update", { description: safeApiError(e) }); }
  };

  const renderComments = (key) => (byLine[key] || []).map((c) => (
    <div key={c.id} data-testid={`room-comment-${c.id}`} className={`mt-1.5 rounded-lg border px-3 py-2 ${c.resolved ? "border-white/10 bg-black/20 opacity-60" : "border-violet-400/25 bg-violet-500/[0.08]"}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-violet-100">{c.author}{c.from_owner ? <span className="ml-1 text-white/40">(writer)</span> : null}{c.resolved ? <span className="ml-2 text-emerald-200/80">· resolved</span> : null}</p>
        {room?.is_owner ? <button type="button" data-testid={`room-resolve-${c.id}`} onClick={() => resolve(c)} className="rounded bg-white/10 p-1 text-white/80 hover:bg-white/20" title={c.resolved ? "Reopen" : "Mark resolved"}><Check className="h-3 w-3" /></button> : null}
      </div>
      <p className="mt-0.5 text-sm text-white/90">{c.text}</p>
      {c.suggestion ? <p className="mt-0.5 text-sm text-emerald-100">→ {c.suggestion}</p> : null}
    </div>
  ));

  return (
    <div data-testid="writing-room-page" className="min-h-screen bg-[#070a16] text-white">
      <header className="border-b border-white/10 bg-[#0b0e1c]/80 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-5 py-4">
          <Link to="/" className="flex items-center gap-2 font-display text-sm font-semibold text-white/85 hover:text-white"><img src="/brand/elpm-logo.png" alt="ELPM Studio" className="h-7 w-auto" /></Link>
          <span className="inline-flex items-center gap-2 rounded-full border border-violet-400/30 bg-violet-500/15 px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-violet-100"><Users className="h-3.5 w-3.5" /> Shared writing room</span>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-5 py-8">
        {error ? (
          <div data-testid="room-error" className="rounded-2xl border border-rose-400/30 bg-rose-500/10 p-6 text-center">
            <p className="font-display text-lg">{error}</p>
            <Link to="/" className="mt-3 inline-block text-sm text-white/70 underline hover:text-white">Go to ELPM Studio</Link>
          </div>
        ) : !room ? (
          <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-300" /></div>
        ) : (
          <>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-violet-300/80">{room.genre || "Lyrics"}</p>
            <h1 data-testid="room-title" className="mt-2 font-display text-3xl font-bold sm:text-4xl">{room.title}</h1>
            <p className="mt-3 text-white/65">
              {room.is_owner ? "This is your room. Co-writers with the link can comment on any line; you resolve comments from here or inside the Lyrics Writer." : "Click any line to leave a comment or suggest a replacement. The writer sees your notes instantly."}
              {room.closed ? <span className="ml-2 rounded bg-white/10 px-2 py-0.5 text-xs text-white/80">Closed to new comments</span> : null}
            </p>

            <div className="mt-8 space-y-7">
              {sections.map((s, si) => (
                <section key={`s-${si}`} data-testid={`room-section-${si}`}>
                  {s.header ? <h2 className="mb-2 font-mono text-xs font-bold uppercase tracking-[0.2em] text-fuchsia-300">[{s.header}]</h2> : null}
                  <div className="space-y-1">
                    {s.lines.map((line, li) => {
                      if (!line.trim()) return <div key={`gap-${si}-${li}`} className="h-3" />;
                      const key = `${si}-${li}`;
                      const n = (byLine[key] || []).filter((c) => !c.resolved).length;
                      return (
                        <div key={key} data-testid={`room-line-${si}-${li}`}>
                          <button
                            type="button"
                            data-testid={`room-line-btn-${si}-${li}`}
                            disabled={room.closed}
                            onClick={() => setTarget((t) => (t?.lineKey === key ? null : { lineKey: key, lineText: line }))}
                            className={`group flex w-full items-start gap-3 rounded-lg px-3 py-1.5 text-left transition-colors hover:bg-white/[0.05] disabled:cursor-default ${target?.lineKey === key ? "bg-violet-500/15 ring-1 ring-violet-400/40" : ""}`}
                          >
                            <span className="flex-1 text-[15px] leading-relaxed text-white/90">{line}</span>
                            <span className={`mt-1 inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${n ? "bg-violet-500/30 text-violet-100" : "invisible bg-white/10 text-white/70 group-hover:visible"}`}>
                              <MessageSquarePlus className="h-3 w-3" /> {n || "comment"}
                            </span>
                          </button>
                          {target?.lineKey === key ? <CommentForm lineKey={key} lineText={line} onSubmit={submit} onCancel={() => setTarget(null)} busy={busy} /> : null}
                          {renderComments(key)}
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>

            <section className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-display text-lg font-bold">Whole-song notes</h3>
                {!room.closed ? (
                  <button type="button" data-testid="room-general-comment-btn" onClick={() => setTarget((t) => (t?.lineKey === "general" ? null : { lineKey: "general", lineText: "" }))} className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.06] px-3 py-1.5 text-sm text-white hover:bg-white/10">
                    {target?.lineKey === "general" ? <X className="h-4 w-4" /> : <MessageSquarePlus className="h-4 w-4" />} {target?.lineKey === "general" ? "Cancel" : "Add a note"}
                  </button>
                ) : null}
              </div>
              {target?.lineKey === "general" ? <CommentForm lineKey="general" lineText="" onSubmit={submit} onCancel={() => setTarget(null)} busy={busy} /> : null}
              {renderComments("general")}
              {!(byLine.general || []).length && target?.lineKey !== "general" ? <p className="mt-2 text-sm text-white/50">No whole-song notes yet.</p> : null}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
